import React, { useState, useEffect, useRef } from 'react';
import { jsPDF } from 'jspdf';
import api from '../services/api';
import BarcodeScanner from '../components/BarcodeScanner';
import { useAuth } from '../context/AuthContext';
import { openPdfPreview } from '../utils/pdfGenerator';
import { clampLinesWithEllipsis, fitTextWithEllipsis } from '../utils/pdfText';
import {
  ShipmentsHeader,
  ShipmentsTable,
  ShipmentsCards,
  ShipmentModal,
  ShipmentPalletPreview
} from '../components/shipments';
import '../styles/Shipments.css';

const Shipments = () => {
  const { isAdmin } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [filteredShipments, setFilteredShipments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [dateFilter, setDateFilter] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('shipmentsViewMode');
    if (saved) return saved;
    return window.innerWidth <= 1024 ? 'cards' : 'table';
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedShipment, setSelectedShipment] = useState(null);

  // Form state
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [papsNumber, setPapsNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedPallets, setSelectedPallets] = useState([]);
  const [availablePallets, setAvailablePallets] = useState([]);
  const availablePalletsRequest = useRef(0);
  const [palletSearch, setPalletSearch] = useState('');
  const [showPalletDropdown, setShowPalletDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showPalletScanner, setShowPalletScanner] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [palletRemovalNotice, setPalletRemovalNotice] = useState('');
  const [selectedCarrierId, setSelectedCarrierId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showAddCarrier, setShowAddCarrier] = useState(false);
  const [newCarrierName, setNewCarrierName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Pallet preview state
  const [showPalletPreview, setShowPalletPreview] = useState(false);
  const [previewPallet, setPreviewPallet] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Pallet photo upload state
  const [uploadingPalletId, setUploadingPalletId] = useState(null);
  const [uploadingBill, setUploadingBill] = useState(false);

  // Refresh state
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchShipments(true);
    fetchCustomers();
    fetchCarriers();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchShipments();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  useEffect(() => {
    if (showPalletDropdown || showPalletScanner) fetchAvailablePallets();
  }, [showPalletDropdown, showPalletScanner]);

  // Auto-open shipment from URL param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shipmentId = params.get('id');
    if (shipmentId && shipments.length > 0) {
      const shipment = shipments.find(s => s.id === parseInt(shipmentId));
      if (shipment) {
        handleViewShipment(shipment);
        window.history.replaceState({}, '', '/shipments');
      }
    }
  }, [shipments]);

  useEffect(() => {
    let filtered = shipments;
    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => (s.status || 'pending') === statusFilter);
    }
    if (dateFilter) {
      filtered = filtered.filter(s => {
        const shipmentDate = new Date(s.created_at).toISOString().split('T')[0];
        return shipmentDate === dateFilter;
      });
    }
    if (searchTerm) {
      filtered = filtered.filter(shipment =>
        shipment.shipment_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shipment.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredShipments(filtered);
  }, [searchTerm, shipments, statusFilter, dateFilter]);

  // API Functions
  const fetchShipments = async (showLoadingSpinner = false) => {
    try {
      if (showLoadingSpinner) setLoading(true);
      const response = await api.get('/shipments');
      setShipments(response.data);
      setFilteredShipments(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load shipments');
    } finally {
      if (showLoadingSpinner) setLoading(false);
    }
  };

  const handleManualRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    await fetchShipments();
    setTimeout(() => setIsRefreshing(false), 600);
  };

  const fetchAvailablePallets = async () => {
    const requestId = ++availablePalletsRequest.current;
    try {
      const response = await api.get('/shipments/available-pallets');
      if (requestId !== availablePalletsRequest.current) return;
      setAvailablePallets(response.data);
    } catch (err) {
      console.error('Failed to load available pallets');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers?status=active');
      setCustomers(response.data);
    } catch (err) {
      console.error('Failed to load customers');
    }
  };

  const fetchCarriers = async () => {
    try {
      const response = await api.get('/carriers');
      setCarriers(response.data);
    } catch (err) {
      // Failed to load carriers
    }
  };

  // Handler Functions
  const handleAddCarrier = async () => {
    if (!newCarrierName.trim()) return;
    try {
      const response = await api.post('/carriers', { name: newCarrierName.trim() });
      await fetchCarriers();
      setSelectedCarrierId(response.data.id);
      setNewCarrierName('');
      setShowAddCarrier(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add carrier');
    }
  };

  const handleCreateShipment = () => {
    setInvoiceNumber('');
    setPoNumber('');
    setPapsNumber('');
    setNotes('');
    setSelectedPallets([]);
    setPalletSearch('');
    setSelectedCustomerId('');
    setPalletRemovalNotice('');
    setSelectedCarrierId('');
    setCustomerSearch('');
    setShowCustomerDropdown(false);
    setModalMode('create');
    fetchAvailablePallets();
    setShowModal(true);
  };

  const handleViewShipment = async (shipment) => {
    try {
      const response = await api.get(`/shipments/${shipment.id}`);
      setSelectedShipment(response.data);
      setModalMode('view');
      setShowModal(true);
    } catch (err) {
      alert('Failed to load shipment details');
    }
  };

  const handleEditShipment = async () => {
    setInvoiceNumber(selectedShipment.invoice_number || '');
    setPoNumber(selectedShipment.po_number || '');
    setPapsNumber(selectedShipment.paps_number || '');
    setNotes(selectedShipment.notes || '');
    setSelectedCarrierId(selectedShipment.carrier_id || '');
    setSelectedPallets(selectedShipment.pallets || []);
    setSelectedCustomerId(selectedShipment.customer_id);
    setPalletSearch('');
    setModalMode('edit');
    await fetchAvailablePallets();
  };

  const handleSaveShipment = async () => {
    if (selectedPallets.length === 0) {
      alert('Please add at least one pallet');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/shipments/${selectedShipment.id}`, {
        invoice_number: invoiceNumber,
        po_number: poNumber || null,
        paps_number: papsNumber.trim(),
        carrier_id: selectedCarrierId || null,
        pallet_ids: selectedPallets.map(p => p.id),
        notes: notes || null
      });
      const shipmentResponse = await api.get(`/shipments/${selectedShipment.id}`);
      setSelectedShipment(shipmentResponse.data);
      setModalMode('view');
      fetchShipments();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update shipment');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = async () => {
    const shipmentResponse = await api.get(`/shipments/${selectedShipment.id}`);
    setSelectedShipment(shipmentResponse.data);
    setModalMode('view');
  };

  const handleMarkAsShipped = async () => {
    if (!window.confirm('Mark this shipment as shipped? This cannot be undone.')) {
      return;
    }
    try {
      await api.patch(`/shipments/${selectedShipment.id}/ship`);
      const shipmentResponse = await api.get(`/shipments/${selectedShipment.id}`);
      setSelectedShipment(shipmentResponse.data);
      fetchShipments();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to mark shipment as shipped');
    }
  };

  const handleConfirmShipment = async () => {
    if (!selectedCustomerId) {
      alert('Please select a customer');
      return;
    }
    if (selectedPallets.length === 0) {
      alert('Please add at least one pallet');
      return;
    }
    const customer = customers.find(c => c.id === parseInt(selectedCustomerId));
    const customerName = customer?.name;
    setSaving(true);
    try {
      const response = await api.post('/shipments', {
        customer_name: customerName,
        invoice_number: invoiceNumber,
        po_number: poNumber || null,
        paps_number: papsNumber.trim(),
        carrier_id: selectedCarrierId || null,
        pallet_ids: selectedPallets.map(p => p.id),
        notes: notes || null
      });
      const newShipmentId = response.data.id;
      const shipmentResponse = await api.get(`/shipments/${newShipmentId}`);
      setSelectedShipment(shipmentResponse.data);
      setModalMode('view');
      fetchShipments();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create shipment');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteShipment = async () => {
    try {
      await api.delete(`/shipments/${selectedShipment.id}`);
      setShowDeleteConfirm(false);
      closeModal();
      fetchShipments();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete shipment');
    }
  };

  // Pallet handlers
  const filteredPallets = availablePallets
    .filter(pallet => {
      const customerIdToMatch = modalMode === 'edit' ? selectedShipment?.customer_id : parseInt(selectedCustomerId);
      return (
        !selectedPallets.find(sp => sp.id === pallet.id) &&
        (customerIdToMatch ? pallet.customer_id === customerIdToMatch : true) &&
        (pallet.pallet_number?.toLowerCase().includes(palletSearch.toLowerCase()) || palletSearch === '')
      );
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const filteredCustomers = customers.filter(customer =>
    customer.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    customer.city?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    customer.state?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const handleSelectPallet = (pallet) => {
    setSelectedPallets([...selectedPallets, pallet]);
    setPalletSearch('');
    setShowPalletDropdown(false);
  };

  const handleVerifyPallet = async (palletId, verifiedBy) => {
    try {
      await api.patch(`/shipments/${selectedShipment.id}/verify-pallet`, {
        pallet_id: palletId,
        verified_by: verifiedBy,
      });
      const shipmentResponse = await api.get(`/shipments/${selectedShipment.id}`);
      setSelectedShipment(shipmentResponse.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to verify pallet');
    }
  };

  const handleSelectCustomer = (customer) => {
    const keptPallets = selectedPallets.filter(p => Number(p.customer_id) === Number(customer.id));
    const removedCount = selectedPallets.length - keptPallets.length;
    setSelectedPallets(keptPallets);
    setPalletRemovalNotice(
      removedCount > 0
        ? `${removedCount} pallet${removedCount === 1 ? '' : 's'} removed — ${removedCount === 1 ? 'it belongs' : 'they belong'} to a different customer.`
        : ''
    );
    setSelectedCustomerId(customer.id);
    setCustomerSearch(`${customer.name} - ${customer.city}, ${customer.state}`);
    setShowCustomerDropdown(false);
    fetchAvailablePallets();
  };

  const handleRemovePallet = (index) => {
    const updated = [...selectedPallets];
    updated.splice(index, 1);
    setSelectedPallets(updated);
  };

  const calculateTotals = () => {
    const totalPallets = selectedPallets.length;
    const totalWeight = selectedPallets.reduce((sum, p) => sum + parseFloat(p.total_weight || 0), 0);
    const totalItems = selectedPallets.reduce((sum, p) => sum + parseInt(p.item_count || 0), 0);
    return { totalPallets, totalWeight, totalItems };
  };

  // Modal/view handlers
  const closeModal = () => {
    setShowModal(false);
    setSelectedShipment(null);
    setSelectedCustomerId('');
    setSelectedCarrierId('');
    setCustomerSearch('');
    setPoNumber('');
    setPapsNumber('');
    setShowDeleteConfirm(false);
    setUploadingBill(false);
    setPalletRemovalNotice('');
  };

  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('shipmentsViewMode', mode);
  };

  // Scanner handlers
  const handleScan = () => setShowScanner(true);

  const handleBarcodeScan = (barcode) => {
    setSearchTerm(barcode);
    setShowScanner(false);
  };

  const handlePalletBarcodeScan = (barcode) => {
    setShowPalletScanner(false);
    const scannedLower = barcode.toLowerCase().trim();
    const palletMatch = scannedLower.match(/^(p\d+)/i);
    const extractedPalletNum = palletMatch ? palletMatch[1].toUpperCase() : null;
    const pallet = availablePallets.find(p => {
      const pNum = p.pallet_number?.toLowerCase();
      const pBarcode = p.barcode?.toLowerCase();
      return (
        pNum === scannedLower ||
        pBarcode === scannedLower ||
        (extractedPalletNum && pNum === extractedPalletNum.toLowerCase()) ||
        scannedLower.startsWith(pNum + '-') ||
        scannedLower.startsWith(pNum + ' ')
      );
    });
    if (pallet) {
      if (selectedPallets.find(sp => sp.id === pallet.id)) {
        alert('Pallet already added to shipment');
        return;
      }
      handleSelectPallet(pallet);
    } else {
      alert(`Pallet "${barcode}" not found or not available`);
    }
  };

  // Pallet preview handlers
  const handleViewPallet = async (palletId, e) => {
    if (e) e.stopPropagation();
    setPreviewLoading(true);
    setShowPalletPreview(true);
    try {
      const response = await api.get(`/pallets/${palletId}`);
      setPreviewPallet(response.data);
    } catch (err) {
      console.error('Failed to load pallet details');
      setShowPalletPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePalletPreview = () => {
    setShowPalletPreview(false);
    setPreviewPallet(null);
  };

  // Pallet photo delete
  const handlePalletPhotoDelete = async (palletId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await api.delete(`/pallets/${palletId}/image`);
      const shipmentResponse = await api.get(`/shipments/${selectedShipment.id}`);
      setSelectedShipment(shipmentResponse.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete photo');
    }
  };

  // Pallet photo upload
  const handlePalletPhotoUpload = async (palletId, event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Only JPG, PNG, and WEBP are allowed.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }
    setUploadingPalletId(palletId);
    try {
      const formData = new FormData();
      formData.append('image', file);
      await api.post(`/pallets/${palletId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const shipmentResponse = await api.get(`/shipments/${selectedShipment.id}`);
      setSelectedShipment(shipmentResponse.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to upload photo');
    } finally {
      setUploadingPalletId(null);
    }
  };

  const handleBillUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'
    ];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Only JPG, PNG, WEBP and PDF are allowed.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }
    setUploadingBill(true);
    try {
      const formData = new FormData();
      formData.append('bill', file);
      await api.post(`/shipments/${selectedShipment.id}/bill`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const shipmentResponse = await api.get(`/shipments/${selectedShipment.id}`);
      setSelectedShipment(shipmentResponse.data);
      fetchShipments();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to upload bill');
    } finally {
      setUploadingBill(false);
    }
  };

  const handleBillDelete = async () => {
    if (!window.confirm('Remove the bill from this shipment? It cannot be marked as shipped without one.')) {
      return;
    }
    setUploadingBill(true);
    try {
      await api.delete(`/shipments/${selectedShipment.id}/bill`);
      const shipmentResponse = await api.get(`/shipments/${selectedShipment.id}`);
      setSelectedShipment(shipmentResponse.data);
      fetchShipments();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove bill');
    } finally {
      setUploadingBill(false);
    }
  };

  // Helper functions
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // PDF Generation
  const generatePackingSlip = async () => {
    if (!selectedShipment) return;
    const doc = new jsPDF({ format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = 12;

    // Logo
    try {
      const logoImg = new Image();
      logoImg.src = '/logo.png';
      await new Promise((resolve, reject) => {
        logoImg.onload = resolve;
        logoImg.onerror = reject;
        setTimeout(reject, 1000);
      });
      doc.addImage(logoImg, 'PNG', 12, y, 20, 20);
    } catch (err) {
    }

    // Company Header
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Montreal Kosher Bakery', 36, y + 6);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Wholesale Bakery Products | www.montrealkosher.com', 36, y + 12);

    // Carrier name - top right corner
    if (selectedShipment.carrier_name) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`Carrier: ${selectedShipment.carrier_name}`, pageWidth - 12, y + 6, { align: 'right' });
    }
    y += 22;

    // PACKING SLIP Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('PACKING SLIP', pageWidth / 2, y, { align: 'center' });
    y += 10;

    // Invoice/Shipment Info Box — a long value shrinks to 6pt, then wraps and grows the box
    const infoValueWidth = 27;
    const infoLineGap = 2.5;
    doc.setFont('helvetica', 'normal');
    const infoRows = [
      ['Invoice #:', selectedShipment.invoice_number || selectedShipment.shipment_number],
      ['Date:', formatDate(selectedShipment.created_at)],
      ['Shipment #:', selectedShipment.shipment_number],
      ['PO #:', selectedShipment.po_number || '-'],
      ['PAPS #:', selectedShipment.paps_number || '-'],
    ].map(([label, value]) => {
      const text = String(value || '-');
      let fontSize = 8;
      while (fontSize > 6 && doc.setFontSize(fontSize).getTextWidth(text) > infoValueWidth) {
        fontSize -= 0.5;
      }
      doc.setFontSize(fontSize);
      return { label, fontSize, lines: doc.splitTextToSize(text, infoValueWidth) };
    });
    const infoExtraHeight = infoRows.reduce((sum, row) => sum + (row.lines.length - 1) * infoLineGap, 0);
    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(250, 250, 250);
    doc.rect(pageWidth - 65, y - 6, 53, 30 + infoExtraHeight, 'FD');
    let infoY = y;
    infoRows.forEach(({ label, fontSize, lines }) => {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text(label, pageWidth - 63, infoY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fontSize);
      lines.forEach((line, i) => doc.text(line, pageWidth - 40, infoY + i * infoLineGap));
      infoY += 5 + (lines.length - 1) * infoLineGap;
    });
    doc.setFontSize(8);

    // Ship To Section
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('SHIP TO:', 12, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(selectedShipment.customer_name || 'Customer Name', 12, y + 5);

    let shipToY = y + 10;
    if (selectedShipment.customer_address) {
      doc.setFontSize(9);
      doc.text(selectedShipment.customer_address, 12, shipToY);
      shipToY += 4;
    }
    if (selectedShipment.customer_city || selectedShipment.customer_state || selectedShipment.customer_zip) {
      const cityStateZip = [
        selectedShipment.customer_city,
        selectedShipment.customer_state,
        selectedShipment.customer_zip
      ].filter(Boolean).join(', ');
      doc.text(cityStateZip, 12, shipToY);
      shipToY += 4;
    }
    if (selectedShipment.customer_country && selectedShipment.customer_country !== 'USA') {
      doc.text(selectedShipment.customer_country, 12, shipToY);
      shipToY += 4;
    }
    y = Math.max(shipToY, y + 23 + infoExtraHeight) + 6;

    // ITEMIZED BREAKDOWN
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('ITEMIZED BREAKDOWN', 15, y);
    y += 7;

    // Aggregate items
    const aggregatedItems = {};
    selectedShipment.items?.forEach(item => {
      const key = item.part_number || 'NO_PART';
      if (!aggregatedItems[key]) {
        aggregatedItems[key] = {
          part_number: item.part_number,
          name: item.name,
          lots: [],
          total_quantity: 0,
          total_weight: 0
        };
      }
      if (item.lot_number && !aggregatedItems[key].lots.includes(item.lot_number)) {
        aggregatedItems[key].lots.push(item.lot_number);
      }
      aggregatedItems[key].total_quantity += parseInt(item.total_quantity || 0);
      aggregatedItems[key].total_weight += parseFloat(item.total_weight || 0);
    });
    const aggregatedArray = Object.values(aggregatedItems);

    // Table Header
    const drawTableHeader = () => {
      doc.setFillColor(220, 220, 220);
      doc.setDrawColor(180, 180, 180);
      doc.rect(10, y - 5, pageWidth - 20, 8, 'FD');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('#', 12, y);
      doc.text('Part #', 18, y);
      doc.text('Item Name', 64, y);
      doc.text('Qty', 142, y);
      doc.text('Lot #', 155, y);
      doc.text('Wt (kg)', pageWidth - 12, y, { align: 'right' });
      y += 8;
      doc.setFont('helvetica', 'normal');
    };

    drawTableHeader();
    doc.setDrawColor(220, 220, 220);
    let summaryTotalQty = 0;
    let summaryTotalWeight = 0;

    const rowBottomLimit = pageHeight - 40;
    const totalsBlockHeight = 30;
    const footerRuleY = pageHeight - 20;
    const totalsFooterGap = 8;
    const totalsBottomLimit = footerRuleY - totalsFooterGap;
    const partNumberMaxWidth = 43;
    const itemNameMaxWidth = 75;
    let rowsOnPage = 0;

    aggregatedArray.forEach((item, index) => {
      const lots = item.lots.join(', ') || '-';
      doc.setFontSize(8);
      const lotLines = doc.splitTextToSize(lots, 20);
      const rowExtraHeight = (lotLines.length - 1) * 5;
      const rowOverflows = y + rowExtraHeight > rowBottomLimit;
      // The last row moves with the totals when they would not fit, so a new page never opens on an empty table
      const totalsOverflow = index === aggregatedArray.length - 1
        && y + rowExtraHeight + 7 + totalsBlockHeight > totalsBottomLimit
        && rowsOnPage > 0;
      if (rowOverflows || totalsOverflow) {
        doc.addPage();
        y = 20;
        drawTableHeader();
        doc.setDrawColor(220, 220, 220);
        rowsOnPage = 0;
      }
      rowsOnPage += 1;

      let partNumber = item.part_number || '-';
      let partFontSize = 9;
      while (partFontSize > 7 && doc.setFontSize(partFontSize).getTextWidth(partNumber) > partNumberMaxWidth) {
        partFontSize -= 0.5;
      }
      if (doc.getTextWidth(partNumber) > partNumberMaxWidth) {
        while (partNumber.length > 1 && doc.getTextWidth(partNumber + '...') > partNumberMaxWidth) {
          partNumber = partNumber.slice(0, -1);
        }
        partNumber += '...';
      }
      doc.text(partNumber, 18, y);

      doc.setFontSize(9);
      doc.text(`${index + 1}`, 12, y);
      doc.text(fitTextWithEllipsis(doc, item.name || '-', itemNameMaxWidth), 64, y);
      doc.text(`${item.total_quantity}`, 142, y);
      doc.text(`${item.total_weight.toFixed(2)}`, pageWidth - 12, y, { align: 'right' });
      doc.setFontSize(8);
      lotLines.forEach((line, i) => doc.text(line, 155, y + i * 5));
      doc.setFontSize(9);
      summaryTotalQty += item.total_quantity;
      summaryTotalWeight += item.total_weight;

      y += (lotLines.length - 1) * 5;
      doc.line(10, y + 2, pageWidth - 10, y + 2);
      y += 7;
    });

    // TOTAL row and totals block stay together, above the footer rule
    if (y + totalsBlockHeight > totalsBottomLimit) {
      doc.addPage();
      y = 20;
    }
    y += 2;
    doc.setDrawColor(100, 100, 100);
    doc.setLineWidth(0.8);
    doc.line(10, y, pageWidth - 10, y);
    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('TOTAL', 12, y);
    doc.text(`${summaryTotalQty}`, 142, y);
    doc.text(`${summaryTotalWeight.toFixed(2)} kg`, pageWidth - 12, y, { align: 'right' });

    // Totals Section
    y += 10;
    const totalCases = selectedShipment.items?.reduce((sum, item) => sum + parseInt(item.total_quantity || 0), 0) || 0;
    const totalsX = pageWidth - 70;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Total Pallets:', totalsX, y);
    doc.text(`${selectedShipment.pallets?.length || 0}`, pageWidth - 12, y, { align: 'right' });
    y += 6;
    doc.text('Total Cases:', totalsX, y);
    doc.text(`${totalCases}`, pageWidth - 12, y, { align: 'right' });
    y += 6;
    doc.setFontSize(12);
    doc.text('Total Weight:', totalsX, y);
    doc.text(`${summaryTotalWeight.toFixed(2)} kg`, pageWidth - 12, y, { align: 'right' });

    // Footer
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setDrawColor(200, 200, 200);
    doc.line(15, footerRuleY, pageWidth - 15, footerRuleY);
    doc.text('Thank you for your business!', pageWidth / 2, pageHeight - 15, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

    openPdfPreview(doc, `PackingSlip-${selectedShipment?.shipment_number || 'Shipment'}.pdf`);
  };

  const generatePalletList = () => {
    if (!selectedShipment) return;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = 15;

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('PALLET LIST', pageWidth / 2, y, { align: 'center' });
    y += 10;

    // Shipment info row
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Shipment: ${selectedShipment.shipment_number}`, 15, y);
    doc.text(`Date: ${formatDate(selectedShipment.created_at)}`, pageWidth - 15, y, { align: 'right' });
    y += 6;

    // Customer and carrier on same line
    doc.setFont('helvetica', 'bold');
    doc.text('Customer:', 15, y);
    doc.setFont('helvetica', 'normal');
    doc.text(selectedShipment.customer_name || 'N/A', 35, y);
    if (selectedShipment.carrier_name) {
      doc.text(`Carrier: ${selectedShipment.carrier_name}`, pageWidth - 15, y, { align: 'right' });
    }
    y += 8;

    // Shipment notes (if any)
    if (selectedShipment.notes) {
      doc.setFillColor(255, 250, 230);
      doc.setDrawColor(200, 180, 100);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const noteWidth = pageWidth - 53;
      const noteLines = clampLinesWithEllipsis(doc, doc.splitTextToSize(selectedShipment.notes, noteWidth), 4, noteWidth);
      const noteBoxHeight = noteLines.length * 5 + 6;
      doc.rect(15, y - 2, pageWidth - 30, noteBoxHeight, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.text('Notes:', 18, y + 3);
      doc.setFont('helvetica', 'normal');
      noteLines.forEach((line, idx) => {
        doc.text(line, 35, y + 3 + (idx * 5));
      });
      y += noteBoxHeight + 4;
    }

    // Divider
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.line(15, y, pageWidth - 15, y);
    y += 6;

    // Pallets header
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Pallets (${selectedShipment.pallets?.length || 0})`, 15, y);
    y += 8;

    // 2 COLUMN LAYOUT
    const colWidth = (pageWidth - 35) / 2;
    const leftCol = 15;
    const rightCol = leftCol + colWidth + 5;
    const pallets = selectedShipment.pallets || [];
    const footerTop = pageHeight - 16;

    const layoutPallet = (pallet) => {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      let itemLines = [];
      if (pallet.item_names) {
        itemLines = clampLinesWithEllipsis(doc, doc.splitTextToSize(pallet.item_names, colWidth - 5), 2, colWidth - 5);
      }
      doc.setFont('helvetica', 'bold');
      const noteLines = pallet.notes
        ? clampLinesWithEllipsis(doc, doc.splitTextToSize(`Note: ${pallet.notes}`, colWidth - 5), 2, colWidth - 5)
        : [];
      return { itemLines, noteLines, height: 5 + itemLines.length * 4 + noteLines.length * 3.5 };
    };

    const drawPallet = (pallet, number, layout, x, startY) => {
      let currentY = startY;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`${number}. ${pallet.pallet_number}`, x, currentY);
      currentY += 5;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      layout.itemLines.forEach(line => {
        doc.text(line, x + 3, currentY);
        currentY += 4;
      });
      doc.setFont('helvetica', 'bold');
      layout.noteLines.forEach(line => {
        doc.text(line, x + 3, currentY);
        currentY += 3.5;
      });
    };

    let rowY = y;
    for (let index = 0; index < pallets.length; index += 2) {
      const left = layoutPallet(pallets[index]);
      const right = pallets[index + 1] ? layoutPallet(pallets[index + 1]) : null;
      const rowHeight = Math.max(left.height, right ? right.height : 0);

      if (rowY + rowHeight > footerTop) {
        doc.addPage();
        rowY = 20;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'italic');
        doc.text(`${selectedShipment.shipment_number} - continued`, pageWidth / 2, 12, { align: 'center' });
      }

      drawPallet(pallets[index], index + 1, left, leftCol, rowY);
      if (right) drawPallet(pallets[index + 1], index + 2, right, rightCol, rowY);
      rowY += rowHeight + 5;
    }

    // Page numbers
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    }

    openPdfPreview(doc, `PalletList-${selectedShipment?.shipment_number || 'Shipment'}.pdf`);
  };

  const totals = calculateTotals();

  // Loading state
  if (loading) {
    return (
      <div className="shipments-page">
        <div className="loading">Loading shipments...</div>
      </div>
    );
  }

  return (
    <div className="shipments-page">
      <ShipmentsHeader
        viewMode={viewMode}
        onToggleView={toggleViewMode}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onScan={handleScan}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
        onCreateClick={handleCreateShipment}
        onRefresh={handleManualRefresh}
        isRefreshing={isRefreshing}
        autoRefresh={autoRefresh}
        onAutoRefreshChange={setAutoRefresh}
      />

      {error && <div className="error-message">{error}</div>}

      {filteredShipments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🚚</div>
          <p className="empty-text">No shipments found</p>
          <p className="empty-subtext">
            {searchTerm ? 'Try a different search term' : 'Create your first shipment to get started'}
          </p>
        </div>
      ) : viewMode === 'table' ? (
        <ShipmentsTable
          shipments={filteredShipments}
          onViewShipment={handleViewShipment}
          formatDate={formatDate}
        />
      ) : (
        <ShipmentsCards
          shipments={filteredShipments}
          onViewShipment={handleViewShipment}
          formatDate={formatDate}
        />
      )}

      <ShipmentModal
        isOpen={showModal}
        onClose={closeModal}
        modalMode={modalMode}
        selectedShipment={selectedShipment}
        invoiceNumber={invoiceNumber}
        setInvoiceNumber={setInvoiceNumber}
        poNumber={poNumber}
        setPoNumber={setPoNumber}
        papsNumber={papsNumber}
        setPapsNumber={setPapsNumber}
        notes={notes}
        setNotes={setNotes}
        selectedPallets={selectedPallets}
        onRemovePallet={handleRemovePallet}
        palletSearch={palletSearch}
        setPalletSearch={setPalletSearch}
        showPalletDropdown={showPalletDropdown}
        setShowPalletDropdown={setShowPalletDropdown}
        filteredPallets={filteredPallets}
        onSelectPallet={handleSelectPallet}
        selectedCustomerId={selectedCustomerId}
        customerSearch={customerSearch}
        setCustomerSearch={setCustomerSearch}
        onCustomerSearchChange={(val) => setSelectedCustomerId('')}
        showCustomerDropdown={showCustomerDropdown}
        setShowCustomerDropdown={setShowCustomerDropdown}
        filteredCustomers={filteredCustomers}
        onSelectCustomer={handleSelectCustomer}
        palletRemovalNotice={palletRemovalNotice}
        selectedCarrierId={selectedCarrierId}
        setSelectedCarrierId={setSelectedCarrierId}
        carriers={carriers}
        showAddCarrier={showAddCarrier}
        setShowAddCarrier={setShowAddCarrier}
        newCarrierName={newCarrierName}
        setNewCarrierName={setNewCarrierName}
        onAddCarrier={handleAddCarrier}
        onViewPallet={handleViewPallet}
        onPalletPhotoUpload={handlePalletPhotoUpload}
        onPalletPhotoDelete={handlePalletPhotoDelete}
        uploadingPalletId={uploadingPalletId}
        onBillUpload={handleBillUpload}
        onBillDelete={handleBillDelete}
        uploadingBill={uploadingBill}
        saving={saving}
        totals={totals}
        onConfirmShipment={handleConfirmShipment}
        onSaveShipment={handleSaveShipment}
        onCancelEdit={handleCancelEdit}
        onEditShipment={handleEditShipment}
        onMarkAsShipped={handleMarkAsShipped}
        onGeneratePackingSlip={generatePackingSlip}
        onGeneratePalletList={generatePalletList}
        showDeleteConfirm={showDeleteConfirm}
        setShowDeleteConfirm={setShowDeleteConfirm}
        onDeleteShipment={handleDeleteShipment}
        isAdmin={isAdmin()}
        onOpenPalletScanner={() => setShowPalletScanner(true)}
        formatDate={formatDate}
        onVerifyPallet={handleVerifyPallet}
      />

      <BarcodeScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleBarcodeScan}
        title="Scan Shipment Barcode"
      />

      <BarcodeScanner
        isOpen={showPalletScanner}
        onClose={() => setShowPalletScanner(false)}
        onScan={handlePalletBarcodeScan}
        title="Scan Pallet Barcode"
      />

      <ShipmentPalletPreview
        isOpen={showPalletPreview}
        onClose={closePalletPreview}
        pallet={previewPallet}
        loading={previewLoading}
      />
    </div>
  );
};

export default Shipments;
